import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  const lastUpdated = "March 17, 2026";

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-slate-700 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-emerald-400" />
            <span className="text-lg font-bold text-slate-100">Trading Journal</span>
          </div>
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-slate-100 mb-2">Terms of Service</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-slate-300 leading-relaxed">
          {/* Important Notice */}
          <section className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-5">
            <h2 className="text-lg font-semibold text-amber-400 mb-2">Important Notice</h2>
            <p className="text-amber-200/80">
              Trading Journal is NOT a registered investment adviser, broker-dealer, or financial
              planner. The Service is for informational and educational purposes only and does
              not constitute investment advice. Past performance is not indicative of future
              results. Trading securities involves substantial risk of loss.
            </p>
          </section>

          {/* 1 - Acceptance */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Trading Journal (the "Service"), you agree to be bound by
              these Terms of Service ("Terms"). If you do not agree to these Terms, you may not
              use the Service.
            </p>
            <p className="mt-3">
              We reserve the right to modify these Terms at any time. Material changes will be
              communicated through the Service. Your continued use after changes constitutes
              acceptance.
            </p>
          </section>

          {/* 2 - Service Description */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">2. Service Description</h2>
            <p>Trading Journal provides:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Personal trade tracking and journaling</li>
              <li>Automated trade data synchronization from connected brokerage accounts</li>
              <li>Performance analytics, statistics, and visualizations</li>
              <li>Optional AI-powered analysis using your own third-party API keys</li>
              <li>Position sizing and risk management tools</li>
            </ul>
            <p className="mt-3">
              The Service is a personal record-keeping and analytical tool. It does not execute
              trades, manage funds, or provide personalized investment recommendations.
            </p>
          </section>

          {/* 3 - Not Financial Advice */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">3. Not Financial Advice</h2>
            <p>
              <strong className="text-slate-100">The Service does not provide financial, investment, tax, or legal
              advice.</strong> All information, analytics, statistics, AI-generated analysis, and
              other content provided through the Service are for informational and educational
              purposes only.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
              <li>
                AI-generated content (including Edge Reports, trade validation, and analysis) is
                algorithmic, may contain errors, and should not be relied upon for trading
                decisions.
              </li>
              <li>
                Performance metrics and statistics reflect historical data and do not predict
                future results.
              </li>
              <li>
                You are solely responsible for your trading and investment decisions.
              </li>
              <li>
                You should consult a qualified financial advisor before making investment
                decisions.
              </li>
            </ul>
          </section>

          {/* 4 - Account Responsibilities */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">4. Account Responsibilities</h2>
            <p>You are responsible for:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activity that occurs under your account</li>
              <li>Ensuring your broker API keys and credentials are kept secure</li>
              <li>Complying with your brokerage's terms of service when connecting accounts</li>
              <li>Providing accurate information when creating your account</li>
            </ul>
            <p className="mt-3">
              You must notify us immediately of any unauthorized use of your account or any
              other security breach.
            </p>
          </section>

          {/* 5 - Acceptable Use */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">5. Acceptable Use</h2>
            <p>You agree NOT to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to reverse engineer, decompile, or disassemble the Service</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Scrape, crawl, or use automated means to access the Service without permission</li>
              <li>Share your account with others or create multiple accounts</li>
              <li>Use the Service to circumvent brokerage restrictions or regulations</li>
              <li>Misrepresent your identity or affiliation</li>
            </ul>
          </section>

          {/* 6 - Broker Integration */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">6. Broker Account Integration</h2>
            <p>
              When you connect a brokerage account, you authorize us to access your trade data
              through the broker's API using the credentials you provide. You acknowledge that:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>We use read-only access where available and never execute trades on your behalf</li>
              <li>Data accuracy depends on the broker's API; we cannot guarantee completeness or timeliness</li>
              <li>You are responsible for complying with your broker's API usage terms</li>
              <li>Broker credentials are encrypted but you share them at your own risk</li>
              <li>You may disconnect your broker account at any time</li>
            </ul>
          </section>

          {/* 7 - AI Features */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">7. AI Analysis Features</h2>
            <p>
              The Service offers optional AI-powered analysis features. By using these features,
              you acknowledge that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>
                <strong>You provide your own API key:</strong> AI features require you to supply
                your own third-party LLM API key (OpenAI, Anthropic, or Google). Usage is
                billed directly by the provider under your account.
              </li>
              <li>
                <strong>Data is sent to third parties:</strong> When you use AI features,
                aggregated trade statistics are sent to the LLM provider. See our{" "}
                <Link to="/privacy" className="text-emerald-400 hover:text-emerald-300 underline">
                  Privacy Policy
                </Link>{" "}
                for details.
              </li>
              <li>
                <strong>AI output is not advice:</strong> AI-generated analysis, rules, and
                insights are algorithmic outputs that may be inaccurate, incomplete, or
                misleading. They do not constitute financial advice.
              </li>
              <li>
                <strong>No guarantees:</strong> We make no representations about the accuracy,
                reliability, or usefulness of AI-generated content.
              </li>
            </ul>
          </section>

          {/* 8 - Subscriptions */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">8. Subscriptions and Billing</h2>
            <p>
              Certain features may require a paid subscription. If you subscribe:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Billing is handled securely through Stripe</li>
              <li>Subscriptions automatically renew at the end of each billing period unless cancelled</li>
              <li>You may cancel your subscription at any time through your account settings</li>
              <li>Cancellation takes effect at the end of the current billing period</li>
              <li>Refunds are handled on a case-by-case basis; contact support for requests</li>
            </ul>
          </section>

          {/* 9 - Data Accuracy */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">9. Data Accuracy</h2>
            <p>
              While we strive for accuracy, trade data synced from brokers, computed analytics,
              and performance metrics may contain errors, delays, or discrepancies. The Service
              is not a system of record for your trades. You should always verify important
              financial data with your brokerage directly.
            </p>
          </section>

          {/* 10 - Intellectual Property */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">10. Intellectual Property</h2>
            <p>
              The Service, including its design, code, features, and documentation, is owned by
              Trading Journal and protected by intellectual property laws. You retain ownership
              of your trade data, journal entries, and other content you create within the Service.
            </p>
          </section>

          {/* 11 - Limitation of Liability */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">11. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRADING JOURNAL AND ITS OPERATORS SHALL
              NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Any trading losses or investment decisions made using the Service</li>
              <li>Inaccuracies in synced trade data, computed analytics, or AI-generated content</li>
              <li>Broker API outages, data delays, or integration failures</li>
              <li>Unauthorized access to your account resulting from your failure to maintain security</li>
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
            </ul>
            <p className="mt-3">
              Our total liability for any claim arising from the Service shall not exceed the
              amount you paid us in the 12 months preceding the claim, or $100, whichever is greater.
            </p>
          </section>

          {/* 12 - Indemnification */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Trading Journal and its operators from
              any claims, damages, losses, or expenses arising from your use of the Service,
              your violation of these Terms, or your violation of any third-party rights.
            </p>
          </section>

          {/* 13 - Termination */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">13. Account Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account if you violate these
              Terms or engage in activity that is harmful to the Service or other users. You may
              delete your account at any time through your account settings.
            </p>
          </section>

          {/* 14 - Governing Law */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">14. Governing Law and Disputes</h2>
            <p>
              These Terms are governed by the laws of the State of California, without regard to
              conflict of law principles. Any disputes shall be resolved through binding
              arbitration in accordance with the rules of the American Arbitration Association,
              except that either party may seek injunctive relief in court.
            </p>
          </section>

          {/* 15 - Severability */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">15. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining
              provisions shall continue in full force and effect.
            </p>
          </section>

          {/* 16 - Contact */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">16. Contact Us</h2>
            <p>
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="mt-2 text-emerald-400">legal@rmultiverse.com</p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-700 flex flex-wrap gap-6 text-sm text-slate-500">
          <Link to="/privacy" className="hover:text-emerald-400 transition-colors">Privacy Policy</Link>
          <Link to="/disclaimer" className="hover:text-emerald-400 transition-colors">Disclaimer</Link>
          <Link to="/login" className="hover:text-emerald-400 transition-colors">Login</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
